import type { AdminReadiness, AdminReadinessStep } from '@fonus/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  CircleDashed,
  KeyRound,
  Lock,
  Mic2,
  RefreshCw,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ListSkeleton, PageTitle } from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Progress } from '../components/ui/progress.tsx';
import { api } from '../lib/api.ts';
import { describe, humanFromApi } from '../lib/errors.ts';
import { credits, num, tashkent, tashkentDate } from '../lib/format.ts';
import { useReadiness } from '../lib/queries.ts';
import { go } from '../lib/router.ts';
import { cn } from '../lib/utils.ts';

const STEP_NAME: Record<AdminReadinessStep['id'], string> = {
  claude: 'Ключ Claude',
  elevenlabs: 'Ключ ElevenLabs',
  voices: 'Голоса выбраны',
  firstStory: 'Первая сказка создана и проверена',
};
const PERM: Record<string, string> = {
  tts: 'Text to Speech',
  voicesRead: 'Voices: Read',
  userRead: 'User: Read',
};

function StatusIcon({ status }: { status: AdminReadinessStep['status'] }) {
  const cls = 'size-6 shrink-0';
  if (status === 'done') return <CheckCircle2 className={cn(cls, 'text-ok')} aria-label="Готово" />;
  if (status === 'warn')
    return <TriangleAlert className={cn(cls, 'text-warn')} aria-label="Внимание" />;
  if (status === 'error')
    return <CircleAlert className={cn(cls, 'text-bad')} aria-label="Ошибка" />;
  if (status === 'locked')
    return <Lock className={cn(cls, 'text-muted')} aria-label="Сначала предыдущий шаг" />;
  return <CircleDashed className={cn(cls, 'text-muted')} aria-label="Не выполнено" />;
}

function KeyHelp({
  open,
  onOpenChange,
  which,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  which: 'claude' | 'elevenlabs';
}) {
  const claude = which === 'claude';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>
          {claude ? 'Как подключить ключ Claude' : 'Как подключить ключ ElevenLabs'}
        </DialogTitle>
        <DialogDescription asChild>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-[15px] text-fg">
            {claude ? (
              <>
                <li>Создайте ключ в console.anthropic.com → API Keys.</li>
              </>
            ) : (
              <>
                <li>
                  В ElevenLabs → Developers → API Keys создайте ключ с правами <b>Text to Speech</b>
                  , <b>Voices: Read</b> и <b>User: Read</b>.
                </li>
              </>
            )}
            <li>
              Откройте Railway → проект fonus-ertak-staging → сервис <b>worker</b> → Variables.
            </li>
            <li>
              Вставьте ключ в переменную{' '}
              <code className="rounded bg-fill px-1">
                {claude ? 'ANTHROPIC_API_KEY' : 'ELEVENLABS_API_KEY'}
              </code>{' '}
              и сохраните — worker перезапустится.
            </li>
            <li>Вернитесь сюда и нажмите «Проверить снова».</li>
          </ol>
        </DialogDescription>
        <p className="mt-4 text-[13px] text-muted">
          Ключи хранятся только в переменных Railway — админка их не видит и не сохраняет.
        </p>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Понятно</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepRow({
  step,
  r,
  onCheck,
  checking,
}: {
  step: AdminReadinessStep;
  r: AdminReadiness;
  onCheck: () => void;
  checking: boolean;
}) {
  const [help, setHelp] = useState(false);
  const d = step.detail;
  const idx = r.steps.findIndex((s) => s.id === step.id);
  const prev = idx > 0 ? r.steps[idx - 1] : undefined;
  let text: React.ReactNode;
  let action: React.ReactNode = null;

  if (step.status === 'locked') {
    text = `Сначала: ${prev ? STEP_NAME[prev.id] : ''}`;
  } else if (step.id === 'claude') {
    const state = d.state as string;
    text =
      step.status === 'done'
        ? 'Подключён'
        : state === 'stub'
          ? 'Не подключён — сейчас пишутся тексты-заготовки'
          : step.status === 'error'
            ? describe(d.code as string).title
            : state === 'unchecked'
              ? 'Ещё не проверялся'
              : 'Не подключён';
    if (step.status !== 'done') {
      action = (
        <>
          <Button size="sm" onClick={() => setHelp(true)}>
            <KeyRound /> Исправить
          </Button>
          <Button size="sm" variant="secondary" disabled={checking} onClick={onCheck}>
            <RefreshCw className={checking ? 'animate-spin' : ''} /> Проверить снова
          </Button>
        </>
      );
    }
  } else if (step.id === 'elevenlabs') {
    const missing = (d.missing as string[] | undefined) ?? [];
    const perms = d.permissions as Record<string, string> | undefined;
    const c = d.credits as { used: number; limit: number } | null;
    if (step.status === 'done') {
      text = (
        <>
          Подключён · права: Voices ✓, User ✓, TTS{' '}
          {perms?.tts === 'ok' ? '✓' : '— проверится при первой озвучке'}
          {c && (
            <>
              {' '}
              · остаток {credits(c.limit - c.used)} из {num(c.limit)}
            </>
          )}
        </>
      );
    } else if (missing.length > 0) {
      text = `Нет права ${missing.map((m) => PERM[m] ?? m).join(', ')} → в ElevenLabs создайте ключ с ${missing.length > 1 ? 'этими правами' : 'этим правом'}`;
    } else if (d.state === 'stub') {
      text = 'Не подключён — сейчас вместо голоса тишина';
    } else if (step.status === 'error') {
      text = describe(d.code as string).title;
    } else {
      text = d.state === 'unchecked' ? 'Ещё не проверялся' : 'Не подключён';
    }
    if (step.status !== 'done') {
      action = (
        <>
          <Button size="sm" onClick={() => setHelp(true)}>
            <KeyRound /> Исправить
          </Button>
          <Button size="sm" variant="secondary" disabled={checking} onClick={onCheck}>
            <RefreshCw className={checking ? 'animate-spin' : ''} /> Проверить снова
          </Button>
        </>
      );
    }
  } else if (step.id === 'voices') {
    const voices = d.voices as Record<string, { voiceName: string } | null>;
    text = (['uz', 'ru', 'en'] as const)
      .map((l) => `${l} ${voices[l] ? '✅' : '❌'}`)
      .join('  ·  ');
    if (step.status !== 'done')
      action = (
        <Button size="sm" onClick={() => go('voices')}>
          <Mic2 /> Перейти
        </Button>
      );
  } else {
    text =
      step.status === 'done'
        ? `Одобрено сказок: ${String(d.approved)}`
        : 'Создайте сказку, послушайте и одобрите её';
    if (step.status !== 'done')
      action = (
        <Button size="sm" onClick={() => go('stories/new')}>
          <Sparkles /> Создать первую сказку
        </Button>
      );
  }

  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-[16px] p-3',
        step.status === 'locked' && 'opacity-55',
      )}
    >
      <StatusIcon status={step.status} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {idx + 1}. {STEP_NAME[step.id]}
        </p>
        <p className={cn('text-[14px]', step.status === 'error' ? 'text-bad' : 'text-muted')}>
          {text}
        </p>
      </div>
      {action && <div className="flex flex-wrap gap-2">{action}</div>}
      {(step.id === 'claude' || step.id === 'elevenlabs') && (
        <KeyHelp open={help} onOpenChange={setHelp} which={step.id} />
      )}
    </li>
  );
}

export function Home({ isOwner }: { isOwner: boolean }) {
  const q = useReadiness();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const check = useMutation({
    mutationFn: () => api('/admin/system/check', { method: 'POST' }),
    onSuccess: () => {
      toast.success('Проверяем ключи…');
      // The worker writes the result within a few seconds.
      for (const ms of [2_500, 6_000])
        setTimeout(() => void qc.invalidateQueries({ queryKey: ['readiness'] }), ms);
    },
    onError: (e) => toast.error(humanFromApi(e).title),
  });

  if (q.isLoading) return <ListSkeleton rows={4} />;
  if (!q.data) return q.error ? <ErrorCard error={humanFromApi(q.error)} /> : null;
  const r = q.data;
  const c = r.credits;
  const monthPct = c.monthLimit > 0 ? (c.monthUsed / c.monthLimit) * 100 : 0;
  const showSteps = !r.ready || expanded;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        title="Главная"
        subtitle={
          r.checkedAt ? `Ключи проверены ${tashkent(r.checkedAt)}` : 'Ключи ещё не проверялись'
        }
      />

      {r.warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          {r.warnings.map((w) => (
            <div
              key={w.id}
              className="flex flex-wrap items-center gap-3 rounded-[16px] border border-warn/30 bg-warn/[.07] p-3"
            >
              <AlarmClock className="size-5 text-warn" />
              <p className="flex-1 text-[15px]">
                {w.id === 'tomorrowNoStory' && (
                  <>
                    Завтра ({tashkentDate(String(w.detail.date))}) нет сказки — дети останутся без
                    новой.
                  </>
                )}
                {w.id === 'creditsLow' && (
                  <>
                    Кредиты ElevenLabs на исходе: осталось {credits(Number(w.detail.left))} из{' '}
                    {num(Number(w.detail.limit))} (меньше 20%).
                  </>
                )}
                {w.id === 'keyBroken' && (
                  <>Ключ {w.detail.claude ? 'Claude' : 'ElevenLabs'} перестал работать.</>
                )}
              </p>
              {w.id === 'tomorrowNoStory' && (
                <Button size="sm" onClick={() => go('stories/new')}>
                  Создать сказку
                </Button>
              )}
              {w.id === 'keyBroken' && (
                <Button size="sm" variant="secondary" onClick={() => setExpanded(true)}>
                  Показать
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Card>
        {showSteps ? (
          <>
            <CardHeader>
              <CardTitle>Готовность</CardTitle>
              <CardDescription>
                Шаги по порядку: следующий открывается, когда готов предыдущий.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="flex flex-col gap-1">
                {r.steps.map((s) => (
                  <StepRow
                    key={s.id}
                    step={s}
                    r={r}
                    checking={check.isPending}
                    onCheck={() => check.mutate()}
                  />
                ))}
              </ol>
              {r.ready && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => setExpanded(false)}
                >
                  Свернуть
                </Button>
              )}
            </CardContent>
          </>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-3 p-4 text-left"
            onClick={() => setExpanded(true)}
          >
            <CheckCircle2 className="size-6 text-ok" />
            <span className="flex-1 font-semibold">
              Система готова
              {c.provider ? ` · остаток ${credits(c.provider.limit - c.provider.used)}` : ''}
            </span>
            <ChevronDown className="size-4 text-muted" />
          </button>
        )}
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Расход кредитов за месяц</CardTitle>
            <CardDescription>
              {credits(c.monthUsed)} из {num(c.monthLimit)} (лимит в настройках)
              {c.provider && (
                <> · на счёте ElevenLabs {credits(c.provider.limit - c.provider.used)}</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress
              value={monthPct}
              tone={monthPct > 80 ? 'bad' : monthPct > 60 ? 'warn' : 'ok'}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Сейчас идёт</CardTitle>
          </CardHeader>
          <CardContent>
            {r.busy ? (
              <div className="flex flex-wrap items-center gap-3">
                <Badge tone="accent">
                  {r.busy.type === 'VOICE_SAMPLE'
                    ? 'Подбор голосов'
                    : r.busy.type === 'GENERATE_STORY'
                      ? 'Генерация'
                      : `Озвучка ${r.busy.lang ?? ''}`}
                </Badge>
                <span className="text-[14px] text-muted">
                  с {tashkent(r.busy.startedAt ?? r.busy.createdAt)}
                </span>
                <Button size="sm" variant="secondary" onClick={() => go('journal')}>
                  Открыть журнал
                </Button>
              </div>
            ) : (
              <p className="text-[14px] text-muted">Ничего не выполняется.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ждут проверки</CardTitle>
          </CardHeader>
          <CardContent>
            {r.inReview > 0 ? (
              <Button size="sm" onClick={() => go('stories?status=IN_REVIEW')}>
                Проверить ({r.inReview})
              </Button>
            ) : (
              <p className="text-[14px] text-muted">Нет сказок на проверке.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Выйдут</CardTitle>
          </CardHeader>
          <CardContent>
            {r.scheduled.length === 0 ? (
              <p className="text-[14px] text-muted">Ничего не запланировано.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {r.scheduled.map((sc) => (
                  <li key={sc.id}>
                    <button
                      type="button"
                      className="text-left text-[15px] hover:text-accent"
                      onClick={() => go(`stories/${sc.id}`)}
                    >
                      {tashkent(sc.publishAt)} · {sc.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
      {!isOwner && (
        <p className="text-[13px] text-muted">Часть действий доступна только владельцу.</p>
      )}
    </div>
  );
}
